import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { api } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, KeyRound, Pencil, UserPlus, Ban, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { v: "Admin", l: "Admin", desc: "Full access, user management" },
  { v: "Manager", l: "Manager", desc: "Approves purchases, view all" },
  { v: "Accountant", l: "Accountant", desc: "Finance, expenses, receipts" },
  { v: "Sales", l: "Sales", desc: "Leads, bookings, receipts" },
  { v: "SiteEngineer", l: "Site Engineer", desc: "Materials, movements, site data" },
];

const ROLE_COLORS = {
  Admin: "bg-red-50 border-red-200 text-red-700",
  Manager: "bg-purple-50 border-purple-200 text-purple-700",
  Accountant: "bg-emerald-50 border-emerald-200 text-emerald-700",
  Sales: "bg-blue-50 border-blue-200 text-blue-700",
  SiteEngineer: "bg-amber-50 border-amber-200 text-amber-700",
};

const EMPTY_NEW = { email: "", name: "", role: "Sales", password: "", project_ids: [] };

export default function Users() {
  const { user: currentUser } = useAuth();

  if (currentUser?.role !== "Admin") {
    return <Navigate to="/" replace />;
  }

  return <UsersInner currentUser={currentUser} />;
}

function UsersInner({ currentUser }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/auth/users")).data,
  });
  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => (await api.get("/projects")).data,
  });

  const filtered = useMemo(() => {
    let list = users;
    if (!showInactive) list = list.filter((u) => u.active !== false);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.role || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [users, search, showInactive]);

  const counts = useMemo(() => {
    const c = { total: users.length, active: 0, byRole: {} };
    for (const u of users) {
      if (u.active !== false) c.active += 1;
      c.byRole[u.role] = (c.byRole[u.role] || 0) + 1;
    }
    return c;
  }, [users]);

  const [openNew, setOpenNew] = useState(false);
  const [newForm, setNewForm] = useState(EMPTY_NEW);
  const [busy, setBusy] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [pwUser, setPwUser] = useState(null);

  async function createUser() {
    if (!newForm.email || !newForm.name || !newForm.password) return toast.error("Email, name and password required");
    if (newForm.password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post("/auth/users", newForm);
      toast.success(`${newForm.role} account created for ${newForm.name}`);
      qc.invalidateQueries({ queryKey: ["users"] });
      setNewForm(EMPTY_NEW);
      setOpenNew(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create user");
    } finally { setBusy(false); }
  }

  async function toggleActive(u) {
    if (u.id === currentUser.id) return toast.error("You cannot deactivate your own account");
    const nowActive = u.active !== false;
    if (nowActive && !window.confirm(`Deactivate ${u.name}? They will not be able to sign in.`)) return;
    try {
      if (nowActive) {
        await api.delete(`/auth/users/${u.id}`);
        toast.success(`${u.name} deactivated`);
      } else {
        await api.patch(`/auth/users/${u.id}`, { active: true });
        toast.success(`${u.name} reactivated`);
      }
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">Administration</div>
          <h1 className="font-heading text-3xl font-bold mt-1">User Management</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Create Manager / Accountant / Sales / Site Engineer logins with role-based access. Only Admins see this page.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <Input
              placeholder="Search name / email / role…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-64 pl-8"
              data-testid="users-search"
            />
          </div>
          <label className="flex items-center gap-1.5 text-xs text-zinc-600 cursor-pointer select-none">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="accent-emerald-700" data-testid="show-inactive" />
            Show inactive
          </label>
          <Button onClick={() => { setNewForm(EMPTY_NEW); setOpenNew(true); }} className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-user-btn">
            <UserPlus size={14} className="mr-1" /> Add User
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card className="bg-white border-zinc-200"><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total Users</div>
          <div className="text-xl font-bold font-mono mt-1">{counts.total} <span className="text-xs text-zinc-400">({counts.active} active)</span></div>
        </CardContent></Card>
        {ROLES.map((r) => (
          <Card key={r.v} className="bg-white border-zinc-200"><CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{r.l}</div>
            <div className="text-xl font-bold font-mono mt-1">{counts.byRole[r.v] || 0}</div>
          </CardContent></Card>
        ))}
      </div>

      {/* New user dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="max-w-lg" data-testid="new-user-dialog">
          <DialogHeader><DialogTitle className="font-heading">New User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Full Name *</Label>
              <Input value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} placeholder="e.g., Sneha Deshmukh" data-testid="new-user-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={newForm.email} onChange={(e) => setNewForm({ ...newForm, email: e.target.value })} placeholder="user@paramdevelopers.in" data-testid="new-user-email" />
            </div>
            <div>
              <Label>Role *</Label>
              <Select value={newForm.role} onValueChange={(v) => setNewForm({ ...newForm, role: v })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r.v} value={r.v}>
                      <div className="flex flex-col">
                        <span>{r.l}</span>
                        <span className="text-[10px] text-zinc-500">{r.desc}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Initial Password * <span className="text-[10px] text-zinc-500 ml-1">min 8 chars — share securely with the user</span></Label>
              <Input type="text" value={newForm.password} onChange={(e) => setNewForm({ ...newForm, password: e.target.value })} placeholder="Welcome@2026" data-testid="new-user-password" />
            </div>
            {newForm.role !== "Admin" && projects.length > 0 && (
              <div>
                <Label>Project Access <span className="text-[10px] text-zinc-500 ml-1">(empty = all projects)</span></Label>
                <div className="grid grid-cols-1 gap-1 max-h-40 overflow-y-auto rounded border border-zinc-200 p-2">
                  {projects.map((p) => {
                    const checked = newForm.project_ids.includes(p.id);
                    return (
                      <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-zinc-50 px-1 py-0.5 rounded">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => setNewForm({
                            ...newForm,
                            project_ids: e.target.checked
                              ? [...newForm.project_ids, p.id]
                              : newForm.project_ids.filter((x) => x !== p.id),
                          })}
                          className="accent-emerald-700"
                        />
                        {p.name}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNew(false)}>Cancel</Button>
            <Button onClick={createUser} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="create-user-btn">
              <UserPlus size={13} className="mr-1" /> {busy ? "Creating…" : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user dialog */}
      {editUser && (
        <EditUserDialog
          user={editUser}
          projects={projects}
          currentUser={currentUser}
          onClose={() => setEditUser(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ["users"] }); setEditUser(null); }}
        />
      )}
      {/* Reset password dialog */}
      {pwUser && (
        <ResetPasswordDialog
          user={pwUser}
          onClose={() => setPwUser(null)}
        />
      )}

      {/* Users table */}
      <Card className="bg-white border-zinc-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-zinc-50"><TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Projects</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((u) => {
                const isMe = u.id === currentUser.id;
                const isActive = u.active !== false;
                const roleCls = ROLE_COLORS[u.role] || "bg-zinc-100 border-zinc-200 text-zinc-700";
                return (
                  <TableRow key={u.id} className="data-row" data-testid={`user-row-${u.email}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-full grid place-items-center text-xs font-semibold ${isActive ? "bg-emerald-100 text-emerald-800" : "bg-zinc-200 text-zinc-500"}`}>
                          {(u.name || "?")[0].toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium">{u.name} {isMe && <span className="text-[10px] text-emerald-600 font-normal ml-1">(you)</span>}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{u.email}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${roleCls}`}>
                        {u.role === "Admin" && <ShieldCheck size={10} />} {u.role}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-zinc-500">
                      {(!u.project_ids || u.project_ids.length === 0) ? (
                        <span className="italic">All projects</span>
                      ) : (
                        <span>{u.project_ids.length} project{u.project_ids.length === 1 ? "" : "s"}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${isActive ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-zinc-100 border-zinc-200 text-zinc-500"}`}>
                        {isActive ? "active" : "deactivated"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" className="mr-1 h-8 px-2" onClick={() => setEditUser(u)} data-testid={`edit-user-${u.email}`} title="Edit user">
                        <Pencil size={12} />
                      </Button>
                      <Button size="sm" variant="outline" className="mr-1 h-8 px-2 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => setPwUser(u)} data-testid={`reset-pw-${u.email}`} title="Reset password">
                        <KeyRound size={12} />
                      </Button>
                      {isActive ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isMe}
                          className="h-8 px-2 border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-40"
                          onClick={() => toggleActive(u)}
                          data-testid={`deactivate-${u.email}`}
                          title={isMe ? "Cannot deactivate yourself" : "Deactivate"}
                        >
                          <Ban size={12} />
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="h-8 px-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => toggleActive(u)} data-testid={`reactivate-${u.email}`} title="Reactivate">
                          <RefreshCw size={12} />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!filtered.length && (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-zinc-500 py-14">
                  No users match your filters.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}


function EditUserDialog({ user, projects, currentUser, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name,
    role: user.role,
    project_ids: user.project_ids || [],
  });
  const [busy, setBusy] = useState(false);
  const isSelf = user.id === currentUser.id;

  async function save() {
    setBusy(true);
    try {
      await api.patch(`/auth/users/${user.id}`, form);
      toast.success("User updated");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-user-dialog">
        <DialogHeader><DialogTitle className="font-heading">Edit User · {user.email}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-user-name" />
          </div>
          <div>
            <Label>Role {isSelf && <span className="text-[10px] text-red-600 ml-1">(cannot demote yourself)</span>}</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} disabled={isSelf}>
              <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.role !== "Admin" && projects.length > 0 && (
            <div>
              <Label>Project Access <span className="text-[10px] text-zinc-500 ml-1">(empty = all projects)</span></Label>
              <div className="grid grid-cols-1 gap-1 max-h-40 overflow-y-auto rounded border border-zinc-200 p-2">
                {projects.map((p) => {
                  const checked = form.project_ids.includes(p.id);
                  return (
                    <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-zinc-50 px-1 py-0.5 rounded">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setForm({
                          ...form,
                          project_ids: e.target.checked
                            ? [...form.project_ids, p.id]
                            : form.project_ids.filter((x) => x !== p.id),
                        })}
                        className="accent-emerald-700"
                      />
                      {p.name}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-edit-user">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ResetPasswordDialog({ user, onClose }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  async function reset() {
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== confirm) return toast.error("Passwords don't match");
    if (!window.confirm(`Reset password for ${user.name}? They will need to sign in with the new password.`)) return;
    setBusy(true);
    try {
      await api.post(`/auth/users/${user.id}/reset-password`, { new_password: pw });
      toast.success(`Password reset for ${user.name}. Share it securely.`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="reset-pw-dialog">
        <DialogHeader><DialogTitle className="font-heading">Reset Password · {user.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            <b>{user.email}</b> will need the new password to sign in. The old password stops working immediately.
          </div>
          <div>
            <Label>New password (min 8 chars)</Label>
            <Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Welcome@2026" data-testid="reset-pw-input" />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input type="text" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-pw-confirm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={reset} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="confirm-reset-pw">
            <KeyRound size={13} className="mr-1" /> {busy ? "Resetting…" : "Reset Password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
